import {
  CSSProperties,
  FormEvent,
  ReactNode,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SiClaude, SiGooglegemini, SiOpenai } from 'react-icons/si';
import {
  Check,
  CircleCheck,
  Clock3,
  Settings2,
  WandSparkles
} from 'lucide-react';
import { cn } from '@/lib/utils';
import useDomain from '@/hooks/useDomain';
import {
  toMcpApiErrorMessage,
  useGetMcpAiSettingsQuery,
  useGetMcpAutoBudgetQuery,
  useListMcpProjectModelProvidersQuery,
  useListMcpProjectModelsQuery,
  useUpdateMcpAiSettingsMutation
} from '../api/mcpApi';
import type { McpProjectAiModel } from '../types/mcpApiTypes';

const PROVIDER_META: Record<
  string,
  { label: string; hint: string; color: string }
> = {
  google: {
    label: 'Gemini',
    hint: 'مدل گوگل؛ سریع و اقتصادی',
    color: '#8E83F5'
  },
  openai: {
    label: 'GPT',
    hint: 'مدل OpenAI؛ قوی در استدلال',
    color: '#10A37F'
  },
  anthropic: {
    label: 'Claude',
    hint: 'مدل Anthropic؛ دقیق و امن',
    color: '#D97706'
  },
  auto: {
    label: 'Auto',
    hint: 'خودکار؛ سهمیهٔ روزانه طبق تنظیمات',
    color: '#6C63FF'
  }
};

const TIER_META: Record<
  string,
  { label: string; hint: string; color: string }
> = {
  high: {
    label: 'قوی',
    hint: 'کیفیت و عمق بیشتر برای تحلیل‌های پیچیده',
    color: '#F5803E'
  },
  mid: {
    label: 'متعادل',
    hint: 'تعادل میان کیفیت پاسخ و سرعت',
    color: '#2C7BE5'
  },
  low: {
    label: 'سریع',
    hint: 'پاسخ سبک و سریع برای کارهای ساده',
    color: '#27BCFD'
  }
};

const TIER_ORDER = ['high', 'mid', 'low'];

const FALLBACK_COLOR = '#748194';

function formatFaNumber(value: number): string {
  return value.toLocaleString('fa-IR');
}

function ProviderIcon({
  provider,
  color,
  size = 22
}: {
  provider: string;
  color: string;
  size?: number;
}) {
  if (provider === 'google') {
    return <SiGooglegemini size={size} color={color} aria-hidden />;
  }
  if (provider === 'anthropic') {
    return <SiClaude size={size} color={color} aria-hidden />;
  }
  if (provider === 'auto') {
    return <WandSparkles size={size} color={color} aria-hidden />;
  }
  return <SiOpenai size={size} color={color} aria-hidden />;
}

function accentStyle(color: string): CSSProperties {
  return { '--tile-accent': color } as CSSProperties;
}

function SettingsSection({
  step,
  title,
  description,
  children
}: {
  step: string;
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section className="mcp-set-section">
      <header className="mcp-set-section-head">
        <span className="mcp-set-step" aria-hidden>
          {step}
        </span>
        <div className="min-w-0">
          <h2 className="mcp-set-section-title">{title}</h2>
          <p className="mcp-set-section-copy">{description}</p>
        </div>
      </header>
      {children}
    </section>
  );
}

function TilesSkeleton({ count, tall }: { count: number; tall?: boolean }) {
  return (
    <div
      className={cn(
        'mcp-set-grid',
        tall ? 'mcp-set-grid--tiers' : 'mcp-set-grid--providers'
      )}
      aria-hidden
    >
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          className={cn('mcp-set-skeleton', tall && 'mcp-set-skeleton--tall')}
        />
      ))}
    </div>
  );
}

export default function McpSettingsPage() {
  const {
    data: userSettings,
    isLoading: settingsLoading,
    error: settingsError
  } = useGetMcpAiSettingsQuery();
  const {
    data: projectModels,
    isLoading: modelsLoading,
    error: modelsError
  } = useListMcpProjectModelsQuery();
  const {
    data: providerConfigs,
    isLoading: providersLoading,
    error: providersError
  } = useListMcpProjectModelProvidersQuery();
  const [provider, setProvider] = useState('google');
  const [tier, setTier] = useState('mid');
  const [message, setMessage] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [justSavedId, setJustSavedId] = useState<string | null>(null);
  const { domain_slug: domainSlug } = useDomain();
  const {
    data: autoBudget,
    isLoading: autoBudgetLoading,
    error: autoBudgetError
  } = useGetMcpAutoBudgetQuery(domainSlug ?? '', {
    refetchOnMountOrArgChange: true,
    skip: provider !== 'auto' || !domainSlug
  });
  const [updateSettings, { isLoading: saving }] =
    useUpdateMcpAiSettingsMutation();

  const models = useMemo(() => projectModels ?? [], [projectModels]);

  const loading = settingsLoading || modelsLoading || providersLoading;
  const error =
    formError ||
    (settingsError
      ? toMcpApiErrorMessage(settingsError, 'خطا در دریافت تنظیمات')
      : null) ||
    (modelsError
      ? toMcpApiErrorMessage(modelsError, 'خطا در دریافت تنظیمات')
      : null) ||
    (providersError
      ? toMcpApiErrorMessage(providersError, 'خطا در دریافت تنظیمات')
      : null);

  const savedModel = useMemo(
    () =>
      userSettings?.projectAiModelId
        ? models.find(model => model.id === userSettings.projectAiModelId)
        : undefined,
    [models, userSettings]
  );

  const savedSelection = useMemo(
    () =>
      userSettings
        ? {
            provider: savedModel?.provider ?? userSettings.provider,
            tier: savedModel?.tier ?? userSettings.tier
          }
        : null,
    [savedModel, userSettings]
  );

  useEffect(() => {
    if (!savedSelection) return;
    setProvider(savedSelection.provider);
    setTier(savedSelection.tier);
  }, [savedSelection]);

  useEffect(() => {
    setJustSavedId(null);
  }, [userSettings]);

  useEffect(() => {
    if (!message) return;
    const timer = window.setTimeout(() => setMessage(null), 3500);
    return () => window.clearTimeout(timer);
  }, [message]);

  const providers = useMemo(() => {
    const fromApi = providerConfigs?.length
      ? providerConfigs.map(config => config.provider)
      : Array.from(new Set(models.map(m => m.provider)));
    return fromApi.length ? fromApi : ['google', 'openai', 'anthropic'];
  }, [models, providerConfigs]);

  const providerConfigByName = useMemo(
    () =>
      new Map((providerConfigs ?? []).map(config => [config.provider, config])),
    [providerConfigs]
  );

  const providerMeta = (value: string) => {
    const fallback = PROVIDER_META[value] ?? {
      label: value,
      hint: '',
      color: FALLBACK_COLOR
    };
    const config = providerConfigByName.get(value);
    return {
      ...fallback,
      label: config?.displayName ?? fallback.label,
      hint: config?.description ?? fallback.hint
    };
  };

  const tiersForProvider = useMemo(() => {
    const available = models
      .filter(m => m.provider === provider)
      .map(m => m.tier);
    return TIER_ORDER.filter(t => available.includes(t));
  }, [models, provider]);

  const isModelSelectable = (model?: McpProjectAiModel | null) =>
    Boolean(model?.isActive);

  const providerIsSelectable = (value: string) => {
    if (value === 'auto') {
      return models.some(m => m.provider === 'auto' && m.isActive);
    }
    return models.some(m => m.provider === value && m.isActive);
  };

  const modelsByTier = useMemo(() => {
    const map = new Map<string, McpProjectAiModel>();
    for (const model of models) {
      if (model.provider !== provider) continue;
      const existing = map.get(model.tier);
      if (!existing || model.sortOrder < existing.sortOrder) {
        map.set(model.tier, model);
      }
    }
    return map;
  }, [models, provider]);

  useEffect(() => {
    if (tiersForProvider.length === 0) return;
    if (!tiersForProvider.includes(tier)) {
      setTier(tiersForProvider.includes('mid') ? 'mid' : tiersForProvider[0]);
    }
  }, [tiersForProvider, tier]);

  const selectedModel = modelsByTier.get(tier);
  const savedModelId = justSavedId ?? userSettings?.projectAiModelId ?? null;
  const dirty = savedModelId
    ? selectedModel?.id !== savedModelId
    : !savedSelection ||
      savedSelection.provider !== provider ||
      savedSelection.tier !== tier;

  const selectProvider = (value: string) => {
    if (!providerIsSelectable(value)) return;
    setMessage(null);
    setFormError(null);
    setProvider(value);
  };

  const selectTier = (value: string) => {
    const model = modelsByTier.get(value);
    if (!isModelSelectable(model)) return;
    setMessage(null);
    setFormError(null);
    setTier(value);
  };

  const onReset = () => {
    if (!savedSelection) return;
    setMessage(null);
    setFormError(null);
    setProvider(savedSelection.provider);
    setTier(savedSelection.tier);
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setMessage(null);
    setFormError(null);
    if (!selectedModel || !isModelSelectable(selectedModel)) {
      setFormError('مدل انتخاب‌شده در دسترس نیست');
      return;
    }
    try {
      await updateSettings({
        projectAiModelId: selectedModel.id
      }).unwrap();
      setJustSavedId(selectedModel.id);
      setMessage('تنظیمات با موفقیت ذخیره شد.');
    } catch (err) {
      setFormError(toMcpApiErrorMessage(err, 'ذخیره ناموفق بود'));
    }
  };

  const budgetPercent =
    autoBudget && autoBudget.limit > 0
      ? Math.min(100, Math.max(0, (autoBudget.used / autoBudget.limit) * 100))
      : 100;
  const budgetExhausted = autoBudget ? autoBudget.remaining <= 0 : false;

  return (
    <div className="mcp-settings-page">
      <style>{`
        .mcp-settings-page {
          --set-ink: var(--bs-body-color, var(--falcon-body-color, #344050));
          --set-muted: var(--bs-secondary-color, var(--falcon-secondary-color, #748194));
          --set-card: var(--bs-card-bg, var(--falcon-card-bg, #fff));
          --set-surface: var(--bs-tertiary-bg, var(--falcon-emphasis-bg, #f9fafd));
          --set-line: var(--bs-border-color, var(--falcon-border-color, rgba(0,0,0,.08)));
          --set-primary: var(--bs-primary, var(--falcon-primary, #2c7be5));
          --set-soft: color-mix(in srgb, var(--set-primary) 12%, transparent);
          --set-hero-a: color-mix(in srgb, var(--set-primary) 16%, transparent);
          --set-hero-b: color-mix(in srgb, var(--set-primary) 10%, transparent);
          --set-success: var(--bs-success, var(--falcon-success, #00d27a));
          --set-warning: var(--bs-warning, var(--falcon-warning, #f5803e));
          --set-danger: var(--bs-danger, var(--falcon-danger, #e63757));
          max-width: 920px;
          margin-inline: auto;
          padding-bottom: 2.5rem;
        }
        [data-bs-theme='dark'] .mcp-settings-page {
          --set-card: var(--falcon-card-bg, var(--bs-card-bg, #0e1c2f));
          --set-surface: var(--falcon-emphasis-bg, var(--bs-tertiary-bg, #121e2d));
          --set-muted: var(--bs-secondary-color, #9da9bb);
          --set-hero-a: color-mix(in srgb, var(--set-primary) 14%, transparent);
          --set-hero-b: color-mix(in srgb, var(--set-primary) 8%, transparent);
        }

        /* Hero */
        .mcp-set-hero {
          position: relative;
          overflow: hidden;
          border-radius: 1.35rem !important;
          border-color: var(--set-line) !important;
          background:
            radial-gradient(1200px 280px at 100% -20%, var(--set-hero-a), transparent 55%),
            radial-gradient(900px 240px at 0% 0%, var(--set-hero-b), transparent 50%),
            var(--set-card);
          margin-bottom: 1.25rem;
        }
        .mcp-set-hero .card-body {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 1rem 1.5rem;
          padding: 1.5rem;
        }
        .mcp-set-hero-icon {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          background: color-mix(in srgb, var(--set-primary) 16%, transparent);
          color: var(--set-primary);
        }
        .mcp-set-hero-title {
          font-size: clamp(1.35rem, 2vw, 1.75rem);
          font-weight: 700;
          letter-spacing: -0.02em;
          margin: 0;
          color: var(--set-ink);
        }
        .mcp-set-hero-copy {
          margin: 0.45rem 0 0;
          color: var(--set-muted);
          font-size: 0.92rem;
          line-height: 1.75;
        }

        /* Panel + sections */
        .mcp-set-panel {
          border-radius: 1.35rem !important;
          border-color: var(--set-line) !important;
        }
        .mcp-set-section {
          padding: 1.5rem;
        }
        .mcp-set-section + .mcp-set-section {
          border-top: 1px solid var(--set-line);
        }
        .mcp-set-section-head {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          margin-bottom: 1.1rem;
        }
        .mcp-set-step {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-size: 0.82rem;
          font-weight: 700;
          background: var(--set-soft);
          color: var(--set-primary);
        }
        .mcp-set-section-title {
          margin: 0;
          font-size: 1rem;
          font-weight: 700;
          color: var(--set-ink);
          line-height: 1.75rem;
        }
        .mcp-set-section-copy {
          margin: 0.1rem 0 0;
          font-size: 0.84rem;
          color: var(--set-muted);
          line-height: 1.7;
        }

        /* Tiles */
        .mcp-set-grid {
          display: grid;
          gap: 0.85rem;
        }
        .mcp-set-grid--providers {
          grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
        }
        .mcp-set-grid--tiers {
          grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
        }
        .mcp-set-tile {
          --tile-accent: var(--set-primary);
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          margin: 0;
          padding: 1rem 1.05rem 1.05rem;
          border: 1px solid var(--set-line);
          border-radius: 1rem;
          background: var(--set-card);
          cursor: pointer;
          transition: border-color .16s ease, box-shadow .16s ease, transform .16s ease, background .16s ease;
          min-width: 0;
        }
        .mcp-set-tile:hover {
          transform: translateY(-2px);
          border-color: color-mix(in srgb, var(--tile-accent) 45%, var(--set-line));
          box-shadow: 0 10px 26px color-mix(in srgb, var(--tile-accent) 12%, transparent);
        }
        [data-bs-theme='dark'] .mcp-set-tile:hover {
          box-shadow: 0 12px 28px rgba(0, 0, 0, 0.35);
        }
        .mcp-set-tile:has(input:focus-visible) {
          outline: 2px solid color-mix(in srgb, var(--tile-accent) 70%, transparent);
          outline-offset: 2px;
        }
        .mcp-set-tile.is-selected {
          border-color: var(--tile-accent);
          background:
            linear-gradient(160deg, color-mix(in srgb, var(--tile-accent) 11%, transparent), transparent 70%),
            var(--set-card);
          box-shadow: 0 0 0 1px var(--tile-accent), 0 10px 26px color-mix(in srgb, var(--tile-accent) 14%, transparent);
        }
        .mcp-set-tile.is-disabled {
          opacity: 0.58;
          cursor: not-allowed;
        }
        .mcp-set-tile.is-disabled:hover {
          transform: none;
          border-color: var(--set-line);
          box-shadow: none;
        }
        .mcp-set-tile-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.5rem;
        }
        .mcp-set-tile-icon {
          width: 42px;
          height: 42px;
          border-radius: 0.8rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          background: color-mix(in srgb, var(--tile-accent) 14%, transparent);
          transition: background .16s ease;
        }
        .mcp-set-tile.is-selected .mcp-set-tile-icon {
          background: color-mix(in srgb, var(--tile-accent) 22%, transparent);
        }
        .mcp-set-check {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          border: 1.5px solid var(--set-line);
          color: transparent;
          transition: background .16s ease, border-color .16s ease, color .16s ease;
        }
        .mcp-set-tile.is-selected .mcp-set-check {
          background: var(--tile-accent);
          border-color: var(--tile-accent);
          color: #fff;
        }
        .mcp-set-tile-title {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.4rem;
          margin-top: 0.2rem;
          font-weight: 700;
          font-size: 0.95rem;
          color: var(--set-ink);
        }
        .mcp-set-tile-hint {
          display: block;
          font-size: 0.8rem;
          line-height: 1.65;
          color: var(--set-muted);
        }
        .mcp-set-tile-lead {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          min-width: 0;
        }
        .mcp-set-tile-lead .mcp-set-tile-title {
          display: block;
          margin-top: 0;
          min-width: 0;
        }
        .mcp-set-tile-lead--wrap {
          flex-wrap: wrap;
          gap: 0.35rem;
        }
        .mcp-set-tile-model {
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          unicode-bidi: isolate;
        }
        .mcp-set-tile-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 0.35rem;
          margin-top: -0.1rem;
        }
        .mcp-set-pill {
          display: inline-flex;
          align-items: center;
          border-radius: 999px;
          padding: 0.12rem 0.55rem;
          font-size: 0.68rem;
          font-weight: 700;
          background: color-mix(in srgb, var(--tile-accent) 14%, transparent);
          color: var(--tile-accent);
        }
        [data-bs-theme='dark'] .mcp-set-pill {
          background: color-mix(in srgb, var(--tile-accent) 24%, transparent);
          color: color-mix(in srgb, var(--tile-accent) 70%, white);
        }

        /* Auto budget */
        .mcp-set-budget {
          margin-top: 0.85rem;
          padding: 0.6rem 0.85rem;
          border-radius: 0.8rem;
          border: 1px solid color-mix(in srgb, #6C63FF 28%, var(--set-line));
          background: color-mix(in srgb, #6C63FF 5%, var(--set-card));
        }
        .mcp-set-budget.is-exhausted {
          border-color: color-mix(in srgb, var(--set-danger) 40%, var(--set-line));
          background: color-mix(in srgb, var(--set-danger) 5%, var(--set-card));
        }
        .mcp-set-budget-row {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.4rem 0.75rem;
        }
        .mcp-set-budget-title {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          margin: 0;
          font-size: 0.82rem;
          font-weight: 700;
          color: var(--set-ink);
          white-space: nowrap;
        }
        .mcp-set-meter {
          flex: 1 1 120px;
          height: 6px;
          border-radius: 999px;
          background: color-mix(in srgb, var(--set-muted) 18%, transparent);
          overflow: hidden;
        }
        .mcp-set-meter-fill {
          height: 100%;
          border-radius: inherit;
          background: #6C63FF;
          transition: width .4s ease;
        }
        .mcp-set-budget.is-exhausted .mcp-set-meter-fill {
          background: var(--set-danger);
        }
        .mcp-set-budget-usage {
          font-size: 0.78rem;
          font-weight: 600;
          color: var(--set-ink);
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
        }
        .mcp-set-budget-badge {
          border-radius: 999px;
          padding: 0.1rem 0.55rem;
          font-size: 0.7rem;
          font-weight: 700;
          white-space: nowrap;
          background: color-mix(in srgb, #6C63FF 14%, transparent);
          color: #6C63FF;
        }
        [data-bs-theme='dark'] .mcp-set-budget-badge {
          background: color-mix(in srgb, #6C63FF 26%, transparent);
          color: color-mix(in srgb, #6C63FF 65%, white);
        }
        .mcp-set-budget.is-exhausted .mcp-set-budget-badge {
          background: color-mix(in srgb, var(--set-danger) 14%, transparent);
          color: var(--set-danger);
        }
        .mcp-set-budget-foot {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.3rem;
          margin-top: 0.4rem;
          font-size: 0.72rem;
          color: var(--set-muted);
        }
        .mcp-set-budget-note {
          color: var(--set-danger);
          font-weight: 600;
        }

        /* Save bar */
        .mcp-set-savebar {
          position: sticky;
          bottom: 0;
          z-index: 2;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          padding: 0.9rem 1.5rem;
          border-top: 1px solid var(--set-line);
          border-radius: 0 0 1.35rem 1.35rem;
          background: color-mix(in srgb, var(--set-card) 92%, transparent);
          backdrop-filter: blur(8px);
        }
        .mcp-set-status {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          font-size: 0.82rem;
          color: var(--set-muted);
        }
        .mcp-set-status.is-dirty { color: var(--set-warning); font-weight: 600; }
        .mcp-set-status.is-saved { color: var(--set-success); font-weight: 600; }
        .mcp-set-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: currentColor;
          box-shadow: 0 0 0 4px color-mix(in srgb, currentColor 20%, transparent);
        }
        .mcp-set-actions {
          display: flex;
          gap: 0.5rem;
          margin-inline-start: auto;
        }

        /* Skeleton */
        .mcp-set-skeleton {
          height: 116px;
          border-radius: 1rem;
          background: linear-gradient(90deg, var(--set-surface) 0%, color-mix(in srgb, var(--set-muted) 12%, var(--set-surface)) 50%, var(--set-surface) 100%);
          background-size: 200% 100%;
          animation: mcp-set-shimmer 1.3s ease-in-out infinite;
        }
        .mcp-set-skeleton--tall { height: 150px; }
        @keyframes mcp-set-shimmer {
          0% { background-position: 100% 0; }
          100% { background-position: -100% 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .mcp-set-tile, .mcp-set-meter-fill { transition: none; }
          .mcp-set-tile:hover { transform: none; }
          .mcp-set-skeleton { animation: none; }
        }
        @media (max-width: 575.98px) {
          .mcp-set-hero .card-body, .mcp-set-section { padding: 1.15rem; }
          .mcp-set-savebar { padding: 0.8rem 1.15rem; }
          }
      `}</style>

      <Card className="mcp-set-hero">
        <Card.Body>
          <div className="d-flex align-items-start gap-3 flex-grow-1 min-w-0">
            <span className="mcp-set-hero-icon">
              <Settings2 size={22} strokeWidth={2.2} aria-hidden />
            </span>
            <div className="flex-grow-1 min-w-0">
              <Card.Title as="h1" className="mcp-set-hero-title">
                تنظیمات دستیار AI
              </Card.Title>
              <Card.Text className="mcp-set-hero-copy">
                مدل و سطح پاسخ دستیار را انتخاب کنید؛ تغییرات از گفتگوی بعدی
                اعمال می‌شود.
              </Card.Text>
            </div>
          </div>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" className="rounded-3 py-2">
          {error}
        </Alert>
      )}

      <Card className="mcp-set-panel">
        <Form onSubmit={event => void onSubmit(event)}>
          <SettingsSection
            step="۱"
            title="ارائه‌دهندهٔ مدل"
            description="موتور هوش مصنوعی که دستیار برای پاسخ به شما از آن استفاده می‌کند."
          >
            {loading ? (
              <TilesSkeleton count={4} />
            ) : (
              <div
                className="mcp-set-grid mcp-set-grid--providers"
                role="radiogroup"
                aria-label="انتخاب ارائه‌دهندهٔ مدل"
              >
                {providers.map(value => {
                  const meta = providerMeta(value);
                  const selected = provider === value;
                  const disabled = !providerIsSelectable(value);
                  const soonTag =
                    disabled && value !== 'auto'
                      ? (models.find(m => m.provider === value)?.tags ?? [])
                      : [];
                  return (
                    <label
                      key={value}
                      className={cn(
                        'mcp-set-tile',
                        selected && 'is-selected',
                        disabled && 'is-disabled'
                      )}
                      style={accentStyle(meta.color)}
                    >
                      <input
                        type="radio"
                        name="mcp-provider"
                        value={value}
                        checked={selected}
                        disabled={disabled}
                        onChange={() => selectProvider(value)}
                        className="visually-hidden"
                      />
                      <span className="mcp-set-tile-head">
                        <span className="mcp-set-tile-lead">
                          <span className="mcp-set-tile-icon">
                            <ProviderIcon provider={value} color={meta.color} />
                          </span>
                          <span className="mcp-set-tile-title text-truncate">
                            {meta.label}
                          </span>
                        </span>
                        <span className="mcp-set-check" aria-hidden>
                          <Check size={13} strokeWidth={3} />
                        </span>
                      </span>
                      {soonTag.length > 0 && (
                        <span className="mcp-set-tile-tags">
                          {soonTag.map(tag => (
                            <span key={tag} className="mcp-set-pill">
                              {tag}
                            </span>
                          ))}
                        </span>
                      )}
                      {meta.hint && (
                        <span className="mcp-set-tile-hint">{meta.hint}</span>
                      )}
                    </label>
                  );
                })}
              </div>
            )}

            {!loading && provider === 'auto' && (
              <div
                className={cn(
                  'mcp-set-budget',
                  budgetExhausted && 'is-exhausted'
                )}
                aria-labelledby="mcp-auto-budget-title"
              >
                <div className="mcp-set-budget-row">
                  <h3
                    id="mcp-auto-budget-title"
                    className="mcp-set-budget-title"
                  >
                    <WandSparkles size={14} color="#6C63FF" aria-hidden />
                    بودجهٔ روزانه
                  </h3>

                  {autoBudgetLoading && (
                    <Spinner animation="border" size="sm" className="ms-auto" />
                  )}

                  {!autoBudgetLoading && !autoBudgetError && autoBudget && (
                    <>
                      <div
                        className="mcp-set-meter"
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={autoBudget.limit}
                        aria-valuenow={Math.min(
                          Math.max(autoBudget.used, 0),
                          Math.max(autoBudget.limit, 0)
                        )}
                        aria-label="مصرف بودجه روزانه Auto"
                      >
                        <div
                          className="mcp-set-meter-fill"
                          style={{ width: `${budgetPercent}%` }}
                        />
                      </div>
                      <span className="mcp-set-budget-usage">
                        {formatFaNumber(autoBudget.used)} از{' '}
                        {formatFaNumber(autoBudget.limit)}
                      </span>
                      <span className="mcp-set-budget-badge">
                        {budgetExhausted
                          ? 'سهمیه تمام شده'
                          : `${formatFaNumber(autoBudget.remaining)} باقی‌مانده`}
                      </span>
                    </>
                  )}
                </div>

                {!domainSlug && (
                  <div className="mcp-set-budget-foot">
                    برای دیدن بودجهٔ روزانه ابتدا یک دامنه انتخاب کنید.
                  </div>
                )}

                {autoBudgetError && (
                  <Alert variant="danger" className="py-1 px-2 mt-2 mb-0 fs--1">
                    {toMcpApiErrorMessage(
                      autoBudgetError,
                      'خطا در دریافت بودجهٔ روزانهٔ Auto'
                    )}
                  </Alert>
                )}

                {!autoBudgetLoading && !autoBudgetError && autoBudget && (
                  <div className="mcp-set-budget-foot">
                    <Clock3 size={12} aria-hidden />
                    {Number.isFinite(autoBudget.hoursUntilReset) && (
                      <>
                        بودجه {formatFaNumber(autoBudget.hoursUntilReset)} ساعت
                        دیگر ریست می‌شود
                      </>
                    )}
                    {budgetExhausted && (
                      <span className="mcp-set-budget-note">
                        · مدل دیگری را انتخاب کنید.
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </SettingsSection>

          {provider !== 'auto' && (
            <SettingsSection
              step="۲"
              title="سطح پاسخ"
              description="بین کیفیت و عمق پاسخ یا سرعت آن، گزینهٔ مناسب کار خود را انتخاب کنید."
            >
              {loading ? (
                <TilesSkeleton count={3} tall />
              ) : (
                <div
                  className="mcp-set-grid mcp-set-grid--tiers"
                  role="radiogroup"
                  aria-label="سطح پاسخ"
                >
                  {(tiersForProvider.length
                    ? tiersForProvider
                    : TIER_ORDER
                  ).map(value => {
                    const meta = TIER_META[value] ?? {
                      label: value,
                      hint: '',
                      color: FALLBACK_COLOR
                    };
                    const model = modelsByTier.get(value);
                    const tags =
                      model && !model.isActive ? (model.tags ?? []) : [];
                    const selected = tier === value;
                    const disabled = !isModelSelectable(model);
                    return (
                      <label
                        key={value}
                        className={cn(
                          'mcp-set-tile',
                          selected && 'is-selected',
                          disabled && 'is-disabled'
                        )}
                        style={accentStyle(meta.color)}
                      >
                        <input
                          type="radio"
                          name="mcp-tier"
                          value={value}
                          checked={selected}
                          disabled={disabled}
                          onChange={() => selectTier(value)}
                          className="visually-hidden"
                        />
                        <span className="mcp-set-tile-head">
                          <span className="mcp-set-tile-lead">
                            <span
                              className="mcp-set-tile-title mcp-set-tile-model"
                              title={model?.displayName}
                            >
                              {model?.displayName ?? meta.label}
                            </span>
                          </span>
                          <span className="mcp-set-check" aria-hidden>
                            <Check size={13} strokeWidth={3} />
                          </span>
                        </span>
                        {tags.length > 0 && (
                          <span className="mcp-set-tile-tags">
                            {tags.map(tag => (
                              <span key={tag} className="mcp-set-pill">
                                {tag}
                              </span>
                            ))}
                          </span>
                        )}
                        <span className="mcp-set-tile-hint">{meta.hint}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </SettingsSection>
          )}

          {!loading && (
            <div className="mcp-set-savebar">
              <span
                className={cn(
                  'mcp-set-status',
                  message ? 'is-saved' : dirty && 'is-dirty'
                )}
                role="status"
                aria-live="polite"
              >
                {message ? (
                  <>
                    <CircleCheck size={16} aria-hidden />
                    {message}
                  </>
                ) : dirty ? (
                  <>
                    <span className="mcp-set-dot" aria-hidden />
                    تغییرات ذخیره نشده است
                  </>
                ) : (
                  'تنظیمات فعلی شما ذخیره شده است'
                )}
              </span>
              <div className="mcp-set-actions">
                {dirty && savedSelection && (
                  <Button
                    type="button"
                    variant="falcon-default"
                    size="sm"
                    onClick={onReset}
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon="undo-alt" className="me-1" />
                    بازگردانی
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="falcon-primary"
                  size="sm"
                  disabled={
                    saving || !dirty || !isModelSelectable(selectedModel)
                  }
                >
                  {saving ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      در حال ذخیره…
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon="save" className="me-1" />
                      ذخیره تنظیمات
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </Form>
      </Card>
    </div>
  );
}
